import {
  select, put, call,
} from 'redux-saga/effects';
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
import url from 'url';
import { makeLoginRedirect } from '@bitComponents/utils/adaByron'
import { CONTENT } from "@bitComponents/unifiedComponents/Stopper";
import { actions as flowActions } from '../reducers/flow';
import { getMinderAndFlowJsonByPathname } from '../flowDefinition/flowNormalizer';
import { commonActions } from '../reducers/common';
import { actions as offerActions } from '../reducers/offer';
import { actions as errorManagementActions } from '../reducers/errorManagement';
import { actions as taggingActions } from '../reducers/tagging';
import { actions as localizedStringsActions } from '../reducers/localizedStrings';
import { actions as apisNeedsActions } from '../reducers/apisNeeds';
import { actions as uiNeedsActions } from '../reducers/uiNeeds';
import { getSpecificLocalizedStringsObj } from '../localizedStrings';
import { getSpecificErrorHanlderObj } from '../errorManagement/webServiceErrorHandler';
import { getHandledErrors } from '../errorManagement/handledErrors';
import { getMyDataType } from './client';
import { isRegisteredShopper as isRegisteredShopperSaga } from './register';
import { SELF_EMPLOYED_SUFFIX } from '../tagging';
import { USER_TYPE } from '../bitConstants/constants';
import { DEVICE_VALUES } from '../utils/catalog';
import { CQ_POTS } from '../catalogConstants';
import { deleteShopper } from '../bitSagas/deleteShopper';

const getContextDataExec = state => state.init.setContextDataExec;
const getContext = state => state.context.data;
const flowStore = state => state.flow;
const uiNeedsStore = state => state.uiNeeds;
const offerStore = state => state.offer;


/**
 * Transforma los nombres de las propiedades de un objeto de parámetros extraídos.
 *
 * Esta función cambia los nombres de las propiedades del objeto `extractedParams` según un conjunto predefinido de reglas.
 *
 * @param {Object} extractedParams - El objeto que contiene los parámetros extraídos.
 * @returns {Object} El objeto con las propiedades renombradas.
 */
const transformExtractedParamsName = (extractedParams) => {
  // Cambiamos el nombre de idCapacidadTelco a storageId
  extractedParams.storageId = extractedParams.idCapacidadTelco;
  delete extractedParams.idCapacidadTelco;

  // Cambiamos el nombre de idColorTelco a colorId
  extractedParams.colorId = extractedParams.idColorTelco;
  delete extractedParams.idColorTelco;

  // Cambiamos el nombre de co a confirmationCo
  extractedParams.confirmationCo = extractedParams.co;
  delete extractedParams.co;

  // Cambiamos el nombre de codigo a cardPaymentErrorCode
  extractedParams.cardPaymentErrorCode = extractedParams.codigo;
  delete extractedParams.codigo;

  // Cambiamos el nombre de dispositivo a r2rType
  extractedParams.r2rType = extractedParams.dispositivo;
  delete extractedParams.dispositivo;
  
  // Transformamos a bool el goToC2C
  extractedParams.goToC2C = extractedParams.goToC2C === 'true' ? true : false;
  
  // Transformamos a bool el showPersonalData
  extractedParams.showPersonalData = extractedParams.showPersonalData === 'true' ? true : false;
  
  // Transformamos a bool el swap
  extractedParams.swap = extractedParams.swap === 'true' ? true : false;

  return extractedParams;
}

/**
 * Generador para manejar los parámetros de la URL.
 *
 * Esta función extrae los parámetros específicos de la URL, los transforma según las reglas definidas,
 * y los devuelve como un objeto. También guarda en redux la cadena de consulta completa si existe.
 *
 * @generator
 * @returns {Object} El objeto con los parámetros extraídos y transformados.
 */
function* handleUrlParameters() {
  // Obtenemos la URL actual del navegador
  const currentUrl = new URL(window.location.href);
  const { searchParams } = currentUrl;

  // Si la URL tiene una cadena de búsqueda, la guardamos
  if (currentUrl.search) {
    yield put(offerActions.saveQueryString(currentUrl.search));
  }

  // Definimos los parámetros que queremos extraer de la URL
  const paramsToExtract = [
    'alias', 'bonus', 'cisi', 'codigo', 'co', 'dispositivo', 'idCapacidadTelco',
    'idColorTelco', 'insurance', 'parentProductId', 'parentProductType', 'poLMA',
    'poN', 'poOTT', 'poR', 'poTV', 'poit', 'productId', 'productOffering', 'productOfferingAlt',
    'subType', 'vval', 'zipCode', 'goToC2C', 'showPersonalData', 'productOfferingsToUpgrade',
    'productOfferingsToDowngrade', 'netflixToDowngrade', 'speedId', 'modality', 'insuranceModality',
    'insurancePo', 'swap', 'quotaId', 'secondaryR2Rs', 'resourceSpecId'
  ];

  // Extraemos los parámetros definidos anteriormente de la URL
  let extractedParams = {};
  for (let param of paramsToExtract) {
    extractedParams[param] = searchParams.get(param);
  }

  const sessionTerminalData = JSON.parse(sessionStorage.getItem('terminalDataInfo'));
    
  
  // Transformamos los nombres de los algunos de los parámetros extraídos
  extractedParams = transformExtractedParamsName(extractedParams);
  
  // Si existen datos de terminal en la sesión, los añadimos a los parámetros extraídos
  if (sessionTerminalData && !extractedParams.productOffering) {
    const { productOffering, colorId, storageId } = sessionTerminalData;
    extractedParams = {
      ...extractedParams,
      productOffering,
      colorId,
      storageId,
    };
  } else {
    sessionStorage.removeItem('terminalDataInfo');
  }
  
  // Devolvemos los parámetros transformados
  return extractedParams;
}

/**
 * Generador para manejar la información de la oferta a partir de los parámetros extraídos.
 *
 * Esta función procesa los parámetros extraídos, maneja casos especiales, y guarda la información
 * de la oferta en redux.
 *
 * @generator
 * @param {Object} extractedParams - Los parámetros extraídos de la URL.
 */
function* handleOfferInfo(extractedParams, clientDataType) {
  // Determinamos si el dispositivo (en caso de que exista) es un r2r incluido
  extractedParams.isDeviceIncluded = extractedParams.r2rType === DEVICE_VALUES.INCLUDED;

  // Dividimos productOffering y promoción (en caso de que venga promoción vendrá separado por espacio)
  if (extractedParams.productOffering && extractedParams.productOffering.includes(' ')) {
    const [productOffering, promotion] = extractedParams.productOffering.split(' ');

    extractedParams.productOffering = productOffering;
    extractedParams.promotion = promotion;
  }

  // Inicializamos secondaryProductOfferings
  extractedParams.secondaryProductOfferings = [];

  // Procesamos las ofertas de producto LMA
  if (extractedParams.poLMA) {
    const lmasProductOfferings = extractedParams.poLMA.split(';');
    extractedParams.secondaryProductOfferings.push(...lmasProductOfferings);
    extractedParams.lmasProductOfferings = lmasProductOfferings;
  }

  if (extractedParams?.alias?.includes('estudiantes')) {
    extractedParams.backToSchool = true;
  }

  // Procesamos la oferta de seguro movil
  if (extractedParams.insurancePo) {
    extractedParams.secondaryProductOfferings.push(extractedParams.insurancePo);
  }

  if (extractedParams.secondaryR2Rs) {
    extractedParams.secondaryR2Rs = extractedParams.secondaryR2Rs.split(',');
  }

  // Procesamos las ofertas de producto de Netflix
  if (extractedParams.poN) {
    const netflixParamsParts = extractedParams.poN.split('_');

    const netflixObj = {
      productOffering: netflixParamsParts[0],
      mode: netflixParamsParts[1],
    };
    extractedParams.secondaryProductOfferings.push(netflixObj.productOffering);
    yield put(offerActions.saveNetflixInfo(netflixObj));
  }

  // Procesamos las ofertas de producto de OTT
  if (extractedParams.poOTT) {
    const ottProducts = extractedParams.poOTT.split(';');

    const ottProductsArray = [];

    ottProducts.forEach(ottProduct => {
      const ottProductParts = ottProduct.split('_');

      if (ottProductParts.length === 2) {
        ottProductsArray.push({
          productOffering: ottProductParts[0],
          mode: ottProductParts[1],
        });
      }

      extractedParams.secondaryProductOfferings.push(ottProductParts[0]);
    });
    
    yield put(offerActions.setOTTProductsInfo(ottProductsArray));
  }

  // Procesamos las ofertas de producto de TV
  let tvProductOfferings = extractedParams.poTV;
  if (tvProductOfferings) {
    tvProductOfferings = tvProductOfferings.split(';');
    extractedParams.secondaryProductOfferings.push(...tvProductOfferings);
  }

  // Procesamos las ofertas de producto R2R
  if (extractedParams.poR) {
    extractedParams.secondaryProductOfferings.push(extractedParams.poR);
  }

  // Procesamos las ofertas de producto de bono
  if (extractedParams.bonus) {
    extractedParams.secondaryProductOfferings.push(extractedParams.bonus);
  }

  // Procesamos las ofertas de productos a dar de alta
  if (extractedParams.productOfferingsToUpgrade) {
    extractedParams.productOfferingsToUpgrade = extractedParams.productOfferingsToUpgrade.split(';');
  }

  // Procesamos las ofertas de productos a dar de baja
  if (extractedParams.productOfferingsToDowngrade) {
    extractedParams.productOfferingsToDowngrade = extractedParams.productOfferingsToDowngrade.split(';');
    extractedParams.productOfferingsToDowngradeForPrices = [...extractedParams.productOfferingsToDowngrade];
  }

  // Inicializamos allPOsToBuy y agregamos las ofertas de producto
  extractedParams.allPOsToBuy = [];
  if (extractedParams.productOffering) {
    extractedParams.allPOsToBuy.push(extractedParams.productOffering);
  }
  extractedParams.allPOsToBuy.push(...extractedParams.secondaryProductOfferings);

  // Guardamos el zipCode
  yield put(taggingActions.setZipCode(extractedParams.zipCode));

  // Creamos una copia de los parámetros a guardar y
  // eliminamos los que no queremos guardar en redux
  const offerToSave = { ...extractedParams };
  delete offerToSave.poLMA;
  delete offerToSave.poN;
  delete offerToSave.poTV;
  delete offerToSave.zipCode;

  // Guardamos la información de la oferta
  yield put(commonActions.setOfferInfo(offerToSave));

  // Procesamos y guardamos el alias si existe
  if (extractedParams.alias && clientDataType && clientDataType.selfEmployed) {
    extractedParams.alias = `${extractedParams.alias}${SELF_EMPLOYED_SUFFIX}`;
  }
  yield put(taggingActions.setAliasSellingProduct(extractedParams.alias));
}

/**
 * Generador para manejar la confirmación de datos a partir de los parámetros extraídos.
 *
 * Esta función procesa los parámetros extraídos, maneja casos especiales y guarda la información
 * de confirmación en el estado de la aplicación.
 *
 * @generator
 * @param {Object} extractedParams - Los parámetros extraídos de la URL.
 * @param {string} extractedParams.co - Código de confirmación del pedido.
 * @param {string} extractedParams.code - Código de error.
 * @returns {Object} El objeto con los parámetros extraídos y posiblemente modificados.
 */
function* handleConfirmationData(extractedParams) {
  // Si existe el parámetro 'co', procesamos los datos de confirmación
  if (extractedParams.confirmationCo) {
    // Recuperamos datos de sessionStorage
    const aliasSellingProduct = sessionStorage.getItem('aliasSellingProduct');
    const pricesInfo = JSON.parse(sessionStorage.getItem('pricesInfo'));
    const choices = sessionStorage.getItem('choices');

    // Guardamos varios datos en el estado de la aplicación
    yield put(taggingActions.setAliasSellingProduct(aliasSellingProduct));
    yield put(uiNeedsActions.setConfirmationInfo({ customerOrder: { code: extractedParams.confirmationCo } }));
    yield put(taggingActions.setCO(extractedParams.confirmationCo));
    yield put(taggingActions.setSelectElementsChoices(choices));
    yield put(uiNeedsActions.setPricesInfo(pricesInfo));
  }

  // Si existen datos de terminal en la sesión, los guardamos para la confirmación
  const sessionTerminalData = JSON.parse(sessionStorage.getItem('terminalDataInfo'));
  if (sessionTerminalData) {
    const vtlConfirmationData = {
      terminalData: sessionTerminalData,
    };
    yield put(apisNeedsActions.setVtlDataForConfirmation(vtlConfirmationData));
  }

  // Devolvemos los parámetros extraídos (posiblemente modificados)
  return extractedParams;
}


/**
 * Generador para manejar la inicialización del flujo a partir del tipo de cliente.
 *
 * Esta función verifica la URL actual, determina el flujo correspondiente según el tipo de usuario
 * y el pathname, y configura el estado inicial del flujo en la aplicación.
 *
 * @generator
 * @param {Object} clientDataType - Tipo de cliente.
 * @param {boolean} clientDataType.selfEmployed - Indica si el cliente es autónomo.
 * @returns {Object|null} La información del flujo si se inicializa correctamente, de lo contrario null.
 */
function* handleFlowInitialization(clientDataType, isRegisteredShopper, showError) {
  // Obtenemos la URL actual del navegador
  const currentUrl = new URL(window.location.href);
  const { userType } = yield select(getContext);
  let pathName = currentUrl.pathname;

  // Eliminamos '/index.html' del pathname si está presente
  if (pathName.indexOf('/index.html') !== -1) {
    pathName = pathName.replace('/index.html', '');
  }

  // Obtenemos la información del flujo y minderJson según el pathname y el tipo de usuario
  const flowInfo = getMinderAndFlowJsonByPathname(pathName, userType, isRegisteredShopper);

  // Configuramos el flujo para autónomos si el cliente es autónomo
  if (clientDataType && clientDataType.selfEmployed) {
    flowInfo.flowJson.isSelfEmployedFlow = true;
  } else {
    flowInfo.flowJson.isSelfEmployedFlow = false;
  }

  if(clientDataType && clientDataType.scope !== "0") {
    yield put(showError({
      errorType: CONTENT.USER_ALERT,

      errorInfo: {
        title: "Inicia sesión con contraseña",
        msg: ["Para consultar esta información necesitas acceder con el documento de identidad del titular y su contraseña."],
        errorName: "usuario light logado",
      },
    }));
    return null;
  }

  // Eliminamos las barras finales del pathname
  const pathnameWithoutTrailingSlashes = pathName.replace(/\/+$/, '');

  // Verificamos si es una inicialización de flujo o confirmación
  const isFlowInit = !!(pathnameWithoutTrailingSlashes === flowInfo.flowJson.resetPath);
  const isConfirmInit = !!(flowInfo.flowJson.confirmationInfo && flowInfo.flowJson.confirmationInfo.paths.some(path => path === pathnameWithoutTrailingSlashes));

  // Redirigimos a la ruta de reinicio si no es inicialización de flujo ni confirmación
  if (!isFlowInit && !isConfirmInit) {
    window.location.href = `${flowInfo.flowJson.resetPath}${currentUrl.search}`;
    return null;
  }

  // Configuramos la dirección de instalación si es necesario
  if (flowInfo && flowInfo.flowJson && flowInfo.flowJson.sendInstallationAddressIfInstallerSelected) {
    yield put(uiNeedsActions.setSendInstallationAddressIfInstallerSelected());
  }

  // Configuramos la información inicial del flujo en el estado de la aplicación
  yield put(flowActions.setInitialFlowInfo({
    ...flowInfo,
    searchParams: currentUrl.search,
    isConfirmInit,
  }));

  // Iniciamos el flujo si es una inicialización de flujo
  if (isFlowInit) {
    yield put(commonActions.setInitFlow());
  }

  // Devolvemos la información del flujo
  return { flowInfo, isConfirmInit };
}


/**
 * Generador para manejar los errores y la localización a partir de la información del flujo.
 *
 * Esta función obtiene la configuración de manejo de errores y los textos localizados
 * según el tipo de usuario y el flujo actual, y los guarda en el estado de la aplicación.
 *
 * @generator
 * @param {Object} flowInfo - La información del flujo actual.
 * @yields {void}
 */
function* handleErrorAndLocalization(flowInfo) {
  // Obtener la configuración para el manejo de errores
  const { userType } = yield select(getContext);
  const { goToC2C } = yield select(offerStore);

  // Obtenemos tanto el userType del cliente como el flujo en el que está navegando
  let flowId = null;
  if (flowInfo && flowInfo.flowJson && flowInfo.flowJson.flowName) {
    flowId = flowInfo.flowJson.flowName;
  }

  // Obtener el manejador de errores específico para los servicios web
  const specificWebServiceErrorHandler = getSpecificErrorHanlderObj(flowId, userType);
  
  // Obtener los errores manejados según el flujo y el tipo de usuario
  const handledErrors = getHandledErrors(flowId, userType, flowInfo.flowJson.isSelfEmployedFlow, goToC2C);

  // Guardar el manejador de errores y los errores manejados en el estado de la aplicación
  yield put(errorManagementActions.setWebServiceErrorHandler(specificWebServiceErrorHandler));
  yield put(errorManagementActions.setHandledErrors(handledErrors));

  // Obtener los textos localizados según el tipo de usuario y el flujo
  const localizedStrings = getSpecificLocalizedStringsObj(flowId, userType, null, flowInfo.flowJson.isSelfEmployedFlow);

  // Guardar los textos localizados en el estado de la aplicación
  yield put(localizedStringsActions.setLocalizedStrings(localizedStrings));
  
  return handledErrors;
}


/**
 * Generador para manejar la inicialización del flujo a partir de los parámetros extraídos y la información del flujo.
 *
 * Esta función verifica los parámetros extraídos para determinar si el flujo puede ser inicializado correctamente
 * y configura el estado inicial del flujo en la aplicación.
 *
 * @generator
 * @param {Object} extractedParams - Los parámetros extraídos de la URL.
 * @param {string} extractedParams.productOffering - Oferta de producto.
 * @param {boolean} extractedParams.isConfirmInit - Indica si es una inicialización de confirmación.
 * @param {string} extractedParams.cisi - Código cisi.
 * @param {Object} flowInfo - La información del flujo actual.
 * @param {Object} flowInfo.flowJson - El JSON del flujo actual.
 * @param {Object} flowInfo.flowJson.confirmationInfo - Información de confirmación del flujo.
 * @param {Array} flowInfo - Objeto con la información del flujo.
 */
function* handleInitFlow(extractedParams, isConfirmInit, flowInfo, showError, handledErrors) {
  // Verificamos si los parámetros necesarios están presentes, de lo contrario mostramos un error
  
  const currentUrl = new URL(window.location.href);
  // Eliminamos las barras finales del pathname
  const pathnameWithoutTrailingSlashes = currentUrl.pathname.replace(/\/+$/, '');

  if (!extractedParams.productOffering
    && !extractedParams.productOfferingIsr
    && !isConfirmInit
    && !extractedParams.cisi
    && !extractedParams.alias
    && !extractedParams.productOfferingsToUpgrade
    && !extractedParams.productOfferingsToDowngrade
    && !extractedParams.netflixToDowngrade
    && !extractedParams.poN
  ) {
    yield put(showError(handledErrors.BUSINESS_ERRORS.INCORRECT_PARAMS));
  } else if (flowInfo.flowJson.confirmationInfo
    && flowInfo.flowJson.confirmationInfo.paths.some(path => path === pathnameWithoutTrailingSlashes)
  ) {
    // Estamos en una confirmación que se puede cargar directamente
    yield put(commonActions.setConfirmedStep(flowInfo.flowJson.confirmationInfo.stepFormMinder));
  } else {
    // Si tenemos el productOffering o la cisi marcamos como confirmado el paso inicial para que comience el flujo
    const initialFlowStep = flowInfo.flowJson.initialStepForMinder;
    yield put(commonActions.setConfirmedStep({ stepId: initialFlowStep }));
  }
}


/**
 * Generador para inicializar el flujo de la aplicación.
 *
 * Esta función verifica si debe ejecutar la inicialización del flujo según el contexto,
 * extrae los parámetros de la URL, maneja la información de la oferta, la confirmación,
 * la inicialización del flujo y la configuración de errores y localización.
 *
 * @generator
 * @param {Object} args - Los argumentos de la función.
 * @param {Function} args.showError - Función para mostrar errores.
 * @returns {null} Siempre retorna null.
 */
export function* initializeFlow(args) {
  const { showError,  } = args;
  const setContextDataExec = yield select(getContextDataExec);

  // Verificar si se debe ejecutar la inicialización del flujo
  if (setContextDataExec === 1) {
    const { userType, userSoftLogged } = yield select(getContext);

    let clientDataType = null;
    let isRegisteredShopper = false;
    // Obtener el tipo de datos del cliente si el tipo de usuario es TELCO
    if(userType === USER_TYPE.TELCO && userSoftLogged){
      makeLoginRedirect();
    } else if (userType === USER_TYPE.TELCO) {
      clientDataType = yield call(getMyDataType);
    } else if (userType === USER_TYPE.OPEN){
      isRegisteredShopper = yield call(isRegisteredShopperSaga);
    }

    // Extraer los parámetros de la URL
    const extractedParams = yield call(handleUrlParameters);

    // Manejar la información de la oferta con los parámetros extraídos
    yield call(handleOfferInfo, extractedParams, clientDataType);

    // Manejar la confirmación de datos con los parámetros extraídos
    yield call(handleConfirmationData, extractedParams);

    // Inicializar el flujo con el tipo de datos del cliente
    const handleFlowInitializationResponse = yield call(handleFlowInitialization, clientDataType, isRegisteredShopper, showError);
    if(handleFlowInitializationResponse){
      const { flowInfo, isConfirmInit } = handleFlowInitializationResponse;
  
      if(flowInfo){
        if(isRegisteredShopper && flowInfo.flowJson.deleteRegisteredShopperAndRefresh){
          yield call(deleteShopper);
          window.location.reload();
        } else {
          // Manejar la configuración de errores y localización con la información del flujo
          const handledErrors = yield call(handleErrorAndLocalization, flowInfo);
      
          // Manejar la inicialización del flujo con los parámetros extraídos y la información del flujo
          yield call(handleInitFlow, extractedParams, isConfirmInit, flowInfo, showError, handledErrors);
        }
      }
    }
  }

  return null;
}


/**
 * Función para verificar la navegación correcta según la ubicación y el estado del flujo.
 *
 * Esta función verifica si es la primera renderización y si la ubicación actual coincide
 * con la ruta esperada del flujo. Si no coincide, redirige al inicio del flujo.
 *
 * @param {Object} payload - El payload que contiene isFirstRendering y location.
 * @param {boolean} payload.isFirstRendering - Indica si es la primera renderización.
 * @param {Object} payload.location - Objeto con la información de la ubicación actual.
 * @param {string} payload.location.pathname - Ruta actual del navegador.
 * @yields {void}
 */
export function* checkCorrectNavigation({ payload }) {
  const { isFirstRendering, location } = payload;
  const { flowJson } = yield select(flowStore);

  // Verificar si no es la primera renderización y si hay información del flujo disponible
  if (!isFirstRendering && flowJson && flowJson.resetPath) {
    const { actualStep } = yield select(uiNeedsStore);

    let pathName = location.pathname;

    // Eliminar '/index.html' del pathname si está presente
    if (pathName.indexOf('/index.html') !== -1) {
      pathName = pathName.replace('/index.html', '');
    }

    // Verificar si la ruta actual no coincide con la ruta esperada del flujo
    if (`${flowJson.resetPath}${actualStep.path}` !== pathName) {
      // Redirigir al inicio del flujo con la búsqueda actual
      window.location.href = `${flowJson.resetPath}${location.search}`;
    }
  }
}

